import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaStore, 
  FaSignOutAlt, 
  FaClock, 
  FaCheckCircle, 
  FaMapMarkerAlt, 
  FaPhone, 
  FaUser, 
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaSpinner,
  FaExclamationTriangle
} from 'react-icons/fa';
import api from '../api';
import './FranchiseDashboard.css';

export default function FranchiseDashboard() {
  const [activeTab, setActiveTab] = useState('pending');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [completingOrder, setCompletingOrder] = useState(null);
  const navigate = useNavigate();

  const itemsPerPage = 10;

  useEffect(() => {
    // Check if user is logged in
    const email = sessionStorage.getItem('franchiseEmail');
    const password = sessionStorage.getItem('franchisePassword');
    
    if (!email || !password) {
      navigate('/franchise/login');
      return;
    }

    fetchOrders();
  }, [navigate]);

  const fetchOrders = async () => {
    const email = sessionStorage.getItem('franchiseEmail');
    const password = sessionStorage.getItem('franchisePassword');
    
    if (!email || !password) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/getStoreOrders', {
        email,
        password
      });

      if (response.data?.status?.statusCode === 'code-200') {
        setOrders(response.data.data || []);
      } else {
        setError('Failed to fetch orders. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteOrder = async (orderId) => {
    const email = sessionStorage.getItem('franchiseEmail');
    const password = sessionStorage.getItem('franchisePassword');
    
    setCompletingOrder(orderId);

    try {
      const response = await api.post('/updateOrderStatus', {
        order_id: orderId,
        email,
        password
      });

      if (response.data?.status?.statusCode === 'code-200') {
        // Refresh orders
        await fetchOrders();
      } else {
        setError('Failed to complete order. Please try again.');
      }
    } catch (error) {
      console.error('Error completing order:', error);
      setError('Network error. Please try again.');
    } finally {
      setCompletingOrder(null);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('franchiseEmail');
    sessionStorage.removeItem('franchisePassword');
    navigate('/franchise/login');
  };

  const filteredOrders = orders.filter(order => 
    activeTab === 'pending' ? order.status === 'pending' : order.status === 'completed'
  );

  const paginatedOrders = activeTab === 'pending' 
    ? filteredOrders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : filteredOrders.slice(0, 20); // Show recent 20 for completed

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    return status === 'pending' ? <FaClock /> : <FaCheckCircle />;
  };

  const getStatusColor = (status) => {
    return status === 'pending' ? '#f59e0b' : '#10b981';
  };

  return (
    <div className="franchise-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <div className="store-info">
            <div className="store-icon">
              <FaStore />
            </div>
            <div>
              <h1>Franchise Dashboard</h1>
              <p>Manage your store orders</p>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <FaSignOutAlt />
            Logout
          </button>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="stats-cards">
          <div className="stat-card pending">
            <div className="stat-icon">
              <FaClock />
            </div>
            <div className="stat-info">
              <h3>{orders.filter(o => o.status === 'pending').length}</h3>
              <p>Pending Orders</p>
            </div>
          </div>
          <div className="stat-card completed">
            <div className="stat-icon">
              <FaCheckCircle />
            </div>
            <div className="stat-info">
              <h3>{orders.filter(o => o.status === 'completed').length}</h3>
              <p>Completed Orders</p>
            </div>
          </div>
          <div className="stat-card total">
            <div className="stat-icon">
              <FaStore />
            </div>
            <div className="stat-info">
              <h3>{orders.length}</h3>
              <p>Total Orders</p>
            </div>
          </div>
        </div>

        <div className="orders-section">
          <div className="section-header">
            <h2>Order Management</h2>
            <div className="tab-buttons">
              <button
                className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('pending');
                  setCurrentPage(1);
                }}
              >
                <FaClock />
                Pending ({orders.filter(o => o.status === 'pending').length})
              </button>
              <button
                className={`tab-btn ${activeTab === 'completed' ? 'active' : ''}`}
                onClick={() => setActiveTab('completed')}
              >
                <FaCheckCircle />
                Completed ({orders.filter(o => o.status === 'completed').length})
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message">
              <FaExclamationTriangle />
              {error}
            </div>
          )}

          {loading ? (
            <div className="loading-container">
              <FaSpinner className="spinner" />
              <p>Loading orders...</p>
            </div>
          ) : (
            <>
              {paginatedOrders.length === 0 ? (
                <div className="no-orders">
                  <div className="no-orders-icon">
                    {activeTab === 'pending' ? <FaClock /> : <FaCheckCircle />}
                  </div>
                  <h3>No {activeTab} orders</h3>
                  <p>
                    {activeTab === 'pending' 
                      ? 'All orders have been completed!' 
                      : 'No completed orders yet.'
                    }
                  </p>
                </div>
              ) : (
                <div className="orders-grid">
                  {paginatedOrders.map((order) => (
                    <div key={order.id} className="order-card">
                      <div className="order-header">
                        <div className="order-status">
                          <span 
                            className="status-badge"
                            style={{ color: getStatusColor(order.status) }}
                          >
                            {getStatusIcon(order.status)}
                            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                          </span>
                        </div>
                        <div className="order-id">#{order.id}</div>
                      </div>

                      <div className="order-content">
                        <div className="order-info">
                          <div className="info-item">
                            <FaUser className="info-icon" />
                            <span>{order.user_name}</span>
                          </div>
                          <div className="info-item">
                            <FaPhone className="info-icon" />
                            <span>{order.user_phone}</span>
                          </div>
                          <div className="info-item">
                            <FaMapMarkerAlt className="info-icon" />
                            <span>{order.address}</span>
                          </div>
                          <div className="info-item">
                            <FaCalendarAlt className="info-icon" />
                            <span>{formatDate(order.order_time)}</span>
                          </div>
                          {order.deliver_time && (
                            <div className="info-item">
                              <FaCheckCircle className="info-icon" />
                              <span>Completed: {formatDate(order.deliver_time)}</span>
                            </div>
                          )}
                        </div>

                        <div className="order-service">
                          <h4>{order.name}</h4>
                        </div>
                      </div>

                      {order.status === 'pending' && (
                        <div className="order-actions">
                          <button
                            className="complete-btn"
                            onClick={() => handleCompleteOrder(order.id)}
                            disabled={completingOrder === order.id}
                          >
                            {completingOrder === order.id ? (
                              <>
                                <FaSpinner className="spinner" />
                                Completing...
                              </>
                            ) : (
                              <>
                                <FaCheckCircle />
                                Mark Complete
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'pending' && totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    <FaChevronLeft />
                    Previous
                  </button>
                  
                  <div className="page-info">
                    Page {currentPage} of {totalPages}
                  </div>
                  
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <FaChevronRight />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
